"use client";

import { useEffect, useState } from "react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";

type Comment = {
  id: number;
  name: string;
  content: string;
  date: string;
  article: { slug: string };
  responses?: Comment[];
};

const CommentsSection = ({ articleId }: { articleId: number }) => {
  return (
    <div className="mt-6 text-left">
      <GoogleReCaptchaProvider
        reCaptchaKey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY!}
      >
        <CommentForm articleId={articleId} />
        <CommentList articleId={articleId} />
      </GoogleReCaptchaProvider>
    </div>
  );
};

// ✅ Fetch Comments (Supports Pagination)
const CommentList = ({ articleId }: { articleId: number }) => {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchComments = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/comments?article=${articleId}&page=${page}&pageSize=5`,
        );
        const data = await response.json();

        if (!response.ok && response.status !== 404) {
          throw new Error("Erreur lors du chargement des commentaires.");
        }

        setComments(data.comments || []);
        setTotalPages(data.meta?.total || 1);
      } catch (err) {
        setError("Impossible de récupérer les commentaires.");
      } finally {
        setLoading(false);
      }
    };

    fetchComments();
  }, [articleId, page]);

  return (
    <div className="mt-6">
      <h3 className="text-lg font-semibold">Commentaires</h3>

      {loading ? (
        <p className="text-gray-500">Chargement...</p>
      ) : error ? (
        <p className="text-red-500">{error}</p>
      ) : comments.length === 0 ? (
        <p className="text-gray-500">Aucun commentaire trouvé.</p>
      ) : (
        <ul className="space-y-4">
          {comments.map((comment) => (
            <CommentItem
              key={comment.id}
              comment={comment}
              articleId={articleId}
            />
          ))}
        </ul>
      )}

      {/* ✅ Pagination Controls */}
      {totalPages > 1 && (
        <div className="mt-4 flex justify-center space-x-2">
          <button
            className={`rounded-lg border px-3 py-1 ${
              page === 1 ? "cursor-not-allowed opacity-50" : "hover:bg-gray-200"
            }`}
            onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
            disabled={page === 1}
          >
            Précédent
          </button>
          <span className="rounded-lg border px-3 py-1">
            {page} / {totalPages}
          </span>
          <button
            className={`rounded-lg border px-3 py-1 ${
              page === totalPages
                ? "cursor-not-allowed opacity-50"
                : "hover:bg-gray-200"
            }`}
            onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={page === totalPages}
          >
            Suivant
          </button>
        </div>
      )}
    </div>
  );
};

// ✅ Display Comment + Replies
const CommentItem = ({
  comment,
  articleId,
}: {
  comment: Comment;
  articleId: number;
}) => {
  const [replying, setReplying] = useState(false);

  return (
    <li className="rounded-lg border bg-white p-3 shadow">
      <p className="text-sm font-semibold">{comment.name}</p>
      <p className="text-sm text-gray-700">{comment.content}</p>
      <p className="text-xs text-gray-500">
        {new Date(comment.date).toLocaleDateString("fr-FR")}
      </p>

      {/* ✅ Show Replies */}
      {comment.responses && comment.responses.length > 0 && (
        <ul className="ml-5 mt-3 space-y-2 border-l-2 pl-3">
          {comment.responses.map((reply) => (
            <CommentItem key={reply.id} comment={reply} articleId={articleId} />
          ))}
        </ul>
      )}

      {/* ✅ Reply Button */}
      <button
        className="mt-2 text-sm text-blue-600 hover:underline"
        onClick={() => setReplying(!replying)}
      >
        Répondre
      </button>

      {replying && <ReplyForm parentId={comment.id} articleId={articleId} />}
    </li>
  );
};

// ✅ Comment Submission Form
const CommentForm = ({ articleId }: { articleId: number }) => {
  const [commentText, setCommentText] = useState("");
  const [commentName, setCommentName] = useState("");
  const [commentEmail, setCommentEmail] = useState("");
  const [message, setMessage] = useState<string | null>(null);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!executeRecaptcha) {
      alert("Erreur de validation reCAPTCHA");
      return;
    }

    const recaptchaToken = await executeRecaptcha("submit_comment");

    const formData = {
      name: commentName,
      email: commentEmail,
      content: commentText,
      article_id: articleId,
      recaptchaToken,
    };

    const response = await fetch("/api/submit-comment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    });

    const data = await response.json();
    if (data.success) {
      setMessage("Commentaire soumis avec succès !");
      setCommentName("");
      setCommentEmail("");
      setCommentText("");
    } else {
      setMessage("Erreur lors de la soumission du commentaire.");
    }
  };

  return (
    <form onSubmit={handleCommentSubmit} className="mt-6 space-y-4">
      <h3 className="text-lg font-semibold">Ajouter un commentaire</h3>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <input
          type="text"
          placeholder="Votre nom"
          className="w-full rounded-lg border p-2"
          value={commentName}
          onChange={(e) => setCommentName(e.target.value)}
          required
        />
        <input
          type="email"
          placeholder="Votre email"
          className="w-full rounded-lg border p-2"
          value={commentEmail}
          onChange={(e) => setCommentEmail(e.target.value)}
          required
        />
      </div>
      <textarea
        className="w-full rounded-lg border p-2"
        placeholder="Écrivez votre commentaire..."
        value={commentText}
        onChange={(e) => setCommentText(e.target.value)}
        required
      />

      {message && <p className="mt-2 text-sm text-gray-700">{message}</p>}

      <button
        type="submit"
        className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-800"
      >
        Envoyer
      </button>
    </form>
  );
};

// ✅ Reply Form
const ReplyForm = ({
  parentId,
  articleId,
}: {
  parentId: number;
  articleId: number;
}) => {
  const [replyText, setReplyText] = useState("");
  const [replyName, setReplyName] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // ✅ Ensure reCAPTCHA is ready before calling it
    if (!executeRecaptcha) {
      alert("Erreur de validation reCAPTCHA. Veuillez réessayer.");
      return;
    }

    try {
      const recaptchaToken = await executeRecaptcha("submit_reply");

      await fetch("/api/submit-comment", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: replyName,
          email: replyEmail,
          content: replyText,
          articleId,
          parent_comment_id: parentId,
          recaptchaToken,
        }),
      });

      // ✅ Reset form after successful submission
      setReplyName("");
      setReplyEmail("");
      setReplyText("");
    } catch (error) {
      alert("Erreur lors de l'envoi de la réponse.");
    }
  };

  return (
    <form onSubmit={handleReplySubmit} className="mt-3 space-y-3">
      {/* 🔹 Name Input */}
      <input
        type="text"
        className="w-full rounded-lg border p-2"
        placeholder="Votre nom"
        value={replyName}
        onChange={(e) => setReplyName(e.target.value)}
        required
      />

      {/* 🔹 Email Input */}
      <input
        type="email"
        className="w-full rounded-lg border p-2"
        placeholder="Votre email"
        value={replyEmail}
        onChange={(e) => setReplyEmail(e.target.value)}
        required
      />

      {/* 🔹 Reply Textarea */}
      <textarea
        className="w-full rounded-lg border p-2"
        placeholder="Réponse..."
        value={replyText}
        onChange={(e) => setReplyText(e.target.value)}
        required
      />

      {/* 🔹 Submit Button */}
      <button
        type="submit"
        className="mt-2 rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-800"
      >
        Répondre
      </button>
    </form>
  );
};

export default CommentsSection;
